import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Create axios instance
const api = axios.create({
  baseURL: `${API_URL}/api`,
  timeout: 30000, // Aumentar timeout para 30 segundos
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Deve estar true para funcionar com CORS
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Fila de espera pra não disparar múltiplos /auth/refresh em paralelo quando
// várias requests tomam 401 ao mesmo tempo (ex.: várias chamadas no mount).
let refreshPromise = null;

const refreshAccessToken = () => {
  if (!refreshPromise) {
    refreshPromise = api
      .post('/auth/refresh')
      .then((response) => {
        const { token } = response.data;
        localStorage.setItem('token', token);
        return token;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Log detalhado de erros
    if (!error.response) {
      console.error('🚨 NETWORK ERROR:', {
        message: error.message,
        code: error.code,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          baseURL: error.config?.baseURL
        }
      });

      // Retorna erro estruturado para erro de rede
      const networkError = new Error('Erro de conexão. Verifique sua internet ou se o servidor está rodando.');
      networkError.response = {
        data: {
          error: 'Erro de conexão com o servidor',
          message: 'Não foi possível conectar ao servidor. Verifique se o backend está rodando.'
        }
      };
      return Promise.reject(networkError);
    }

    const originalRequest = error.config;
    const isLoginRequest = originalRequest?.url?.includes('/auth/login');
    const isRefreshRequest = originalRequest?.url?.includes('/auth/refresh');

    // Sessão persistente: se o access token expirou mas ainda existe um
    // refresh token válido (cookie httpOnly, enviado automaticamente), tenta
    // renovar silenciosamente e refazer a request original antes de deslogar.
    // É isso que evita pedir login de novo toda vez que o app instalado é aberto.
    if (
      error.response?.status === 401 &&
      !isLoginRequest &&
      !isRefreshRequest &&
      !originalRequest?._retry
    ) {
      originalRequest._retry = true;
      try {
        const token = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('token');
        window.location.href = '/app/login';
        return Promise.reject(refreshError);
      }
    }

    // Log de erros HTTP
    console.error('❌ HTTP ERROR:', {
      status: error.response?.status,
      data: error.response?.data,
      url: error.config?.url
    });

    if (error.response?.status === 401 && !isLoginRequest) {
      // Clear token and redirect to login
      localStorage.removeItem('token');
      window.location.href = '/app/login';
    }

    return Promise.reject(error);
  }
);

export default api;